import { useQuery } from "@tanstack/react-query";
import { CustomersAPI } from "../api/customers";
import type { CustomerSummary } from "../types";
import { customerKeys } from "./use-customers";
import { useApiScope, type ApiScope } from "@/features/utils/api-scope";
import { isDemoMode } from "@/features/utils/demo-mode";
import { getMockCustomer } from "./use-customers.mock";

/** The server's cap on one batch (`MAX_SUMMARIES`). */
const BATCH_SIZE = 100;

type Waiter = {
  resolve: (customer: CustomerSummary | null) => void;
  reject: (error: unknown) => void;
};

/**
 * One pending batch per tenant and scope: every `useCustomerSummary` asked for in the
 * same tick of rendering joins it, and it leaves as one request (`GET …/customers/batch`)
 * instead of one per row.
 */
const batches = new Map<string, Map<string, Waiter[]>>();

const flush = async (clientId: string, scope: ApiScope, key: string) => {
  const pending = batches.get(key);
  batches.delete(key);
  if (!pending) return;

  const ids = [...pending.keys()];
  for (let i = 0; i < ids.length; i += BATCH_SIZE) {
    const chunk = ids.slice(i, i + BATCH_SIZE);
    try {
      const { customers } = await CustomersAPI.getCustomerSummaries(
        clientId,
        chunk,
        scope,
      );
      const byId = new Map(customers.map((c) => [c.id, c]));
      for (const id of chunk) {
        for (const waiter of pending.get(id) || []) {
          waiter.resolve(byId.get(id) ?? null);
        }
      }
    } catch (error) {
      for (const id of chunk) {
        for (const waiter of pending.get(id) || []) waiter.reject(error);
      }
    }
  }
};

const loadSummary = (
  clientId: string,
  customerId: string,
  scope: ApiScope,
): Promise<CustomerSummary | null> => {
  const key = `${scope}:${clientId}`;
  let pending = batches.get(key);
  if (!pending) {
    pending = new Map();
    batches.set(key, pending);
    setTimeout(() => void flush(clientId, scope, key), 0);
  }
  const waiters = pending.get(customerId) || [];
  pending.set(customerId, waiters);
  return new Promise((resolve, reject) => waiters.push({ resolve, reject }));
};

/**
 * A customer as a list row shows them — name, "?" and contacts — batched with every
 * other row rendered at the same time: a page of 30 conversations is one request, not
 * 30 detail reads. Its own cache key, never `useCustomer`'s: a summary is not the record
 * (no callback preferences, no postal address), and a detail page reading one would
 * find fields missing.
 */
export function useCustomerSummary(clientId: string, customerId: string) {
  const scope = useApiScope();
  return useQuery<CustomerSummary | null>({
    queryKey: [...customerKeys.summary(clientId, customerId), scope],
    queryFn: async () => {
      if (isDemoMode()) return getMockCustomer(customerId).customer;
      return loadSummary(clientId, customerId, scope);
    },
    staleTime: 30 * 1000,
    enabled: !!clientId && !!customerId,
  });
}
